package fr.frux.feature.map

import fr.frux.core.common.logging.Logger
import fr.frux.core.common.prefs.AppPreferences
import fr.frux.core.common.result.runCatchingCancellable
import fr.frux.shared.domain.model.MapFilter
import fr.frux.shared.domain.model.Tree
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update

/** Clé de la pastille de zone peuplée : posée dès qu'elle a été vue ou rendue inutile. */
internal const val POPULATED_HINT_SEEN_KEY = "map_populated_hint_seen"

/**
 * Mémoire de la pastille « La carte grandit grâce aux habitants » (spec onboarding de
 * contribution, lot 3). Une lecture ratée vaut « déjà vue » : un stockage cassé ne doit pas
 * rouvrir la pastille à chaque lancement.
 */
class PopulatedAreaHintStore(
    private val preferences: AppPreferences,
) {
    suspend fun isSeen(): Boolean =
        runCatchingCancellable { preferences.getBoolean(POPULATED_HINT_SEEN_KEY, false) }
            .onFailure { Logger.e(TAG, "Lecture de la pastille de zone peuplée impossible", it) }
            .getOrDefault(true)

    suspend fun markSeen() {
        runCatchingCancellable { preferences.putBoolean(POPULATED_HINT_SEEN_KEY, true) }
            .onFailure { Logger.e(TAG, "Écriture de la pastille de zone peuplée impossible", it) }
    }

    suspend fun clear() {
        runCatchingCancellable { preferences.remove(POPULATED_HINT_SEEN_KEY) }
            .onFailure { Logger.e(TAG, "Purge de la pastille de zone peuplée impossible", it) }
    }

    private companion object {
        const val TAG = "PopulatedAreaHintStore"
    }
}

/**
 * Pastille de première session en zone peuplée, sortie de [MapViewModel] (ratchet detekt
 * `LargeClass`). Lit le store une fois, puis tient le verdict en mémoire comme
 * [EmptyAreaDismissController]. Elle ne s'affiche qu'une fois : la zone vide et l'ajout d'un arbre
 * la rendent inutile, ils la marquent vue sans l'afficher.
 */
internal class PopulatedAreaHintController(
    viewStore: MapViewStore,
    private val launchForUser: (suspend CoroutineScope.() -> Unit) -> Job,
    private val state: MutableStateFlow<MapState>,
) {
    private val store = viewStore.populatedHints
    private var seen: Boolean? = null

    /** Appelé au succès d'un chargement, après la mise à jour de l'état (la zone vide y est déjà posée). */
    suspend fun onTreesLoaded(
        trees: List<Tree>,
        requestedFilter: MapFilter,
    ) {
        if (state.value.showsEmptyAreaCard) onEmptyAreaShown()
        val filtered = requestedFilter.query.isActive || requestedFilter.rollupId != null
        if (trees.isEmpty() || filtered || isSeen()) return
        state.update { it.copy(populatedHint = true) }
        markSeen()
    }

    fun onEmptyAreaShown() {
        launchForUser { markSeen() }
    }

    fun onTreeAdded() {
        launchForUser { markSeen() }
    }

    fun dismiss() {
        state.update { it.copy(populatedHint = false) }
    }

    /** Déconnexion : le compte suivant sur l'appareil repart de zéro. */
    fun forget() {
        state.update { it.copy(populatedHint = false) }
        seen = false
        launchForUser { store.clear() }
    }

    private suspend fun isSeen(): Boolean = seen ?: store.isSeen().also { seen = it }

    private suspend fun markSeen() {
        seen = true
        store.markSeen()
    }
}
